import React, { useState } from 'react'
import { Box, Typography, Button } from '@mui/material'
import { MAX_PIN_LENGTH } from '@/shared/constants'
import { useTranslation } from 'react-i18next'
import InputErrorPopUp from '@/pages/loginScreen/InputErrorPopUp'
import { RoundedInputField } from '@/shared/ui/RoundedInputField'
import useKeyDown from '@/shared/hooks/useKeyDown'

interface Props {
    onCheckPin: () => void
    onChangePin: (value: string) => void
    onCancelPin: () => void
    isPinError: boolean
    width?: string
}

const PinInputField: React.FC<Props> = ({
    onChangePin,
    onCheckPin,
    onCancelPin,
    isPinError,
    width,
}) => {
    const [pin, setPin] = useState('')
    const isEnterPinDisabled = pin.length !== MAX_PIN_LENGTH
    const { t: translate } = useTranslation()

    const editContainer = {
        position: 'relative',
        padding: '0 8px',
        width: width ?? 'auto',
    }
    const handleProfileStyle = {
        marginTop: '12px',
        width: '100%',
        height: '36px',
        padding: '8px 12px',
        gap: '8px',
        borderRadius: '10px',
        bgcolor: 'primary.main',
    }
    const cancelButtonStyle = {
        marginTop: '12px',
        width: '100%',
        height: '36px',
        padding: '8px 12px',
        gap: '8px',
        borderRadius: '10px',
        bgcolor: 'transparent',
        border: '1px solid white',
        color: 'white',
    }
    const enterPinStyleStyle = {
        ...handleProfileStyle,
        bgcolor: isEnterPinDisabled ? 'custom.lightGray' : 'primary.main',
    }

    const handleInputSetValue = (value: string) => {
        if (value.length <= MAX_PIN_LENGTH && /^\d*$/.test(value)) {
            setPin(value)
            onChangePin(value)
        }
    }
    const inputFieldStyle = {
        background: 'none',
        width: '50%',
        padding: '10px',
        borderRadius: '8px',
        border: '1px solid grey',
        color: 'white',
        margin: '0 auto',
        marginTop: '5px',
        outline: 'none',
    }
    useKeyDown(onCheckPin)

    return (
        <Box sx={editContainer} data-testid="profileScreen">
            <Box mb="16px">
                <Typography variant="label3" color="white">
                    {translate('pinRequired')}
                </Typography>
            </Box>

            <RoundedInputField
                searchValue={pin}
                setSearchValue={handleInputSetValue}
                placeholder="PIN"
                native={{
                    autoComplete: 'off',
                    type: 'text',
                    name: 'pin',
                    maxLength: 4,
                    size: 4,
                }}
                sx={inputFieldStyle}
            />

            {isPinError && (
                <InputErrorPopUp
                    styles={{
                        position: 'absolute',
                        top: '78px',
                        left: '15px',
                        width: 'max-content',
                    }}
                    errorMessage={translate('invalidPin')}
                />
            )}
            <Box display="flex" columnGap="16px" mt="16px">
                <Button
                    data-testid="accept-button"
                    disabled={isEnterPinDisabled}
                    sx={enterPinStyleStyle}
                    onClick={onCheckPin}>
                    <Typography color="black" variant="label4">
                        {translate('accept')}
                    </Typography>
                </Button>
                <Button
                    data-testid="cancel-button"
                    variant="outlined"
                    sx={cancelButtonStyle}
                    onClick={onCancelPin}>
                    <Typography color="white" variant="label4">
                        {translate('globalCancel')}
                    </Typography>
                </Button>
            </Box>
        </Box>
    )
}

export default PinInputField
